import {EntityAction, EntityActionOptions, EntityCollectionServiceBase, EntityOp} from "@ngrx/data";
import {BehaviorSubject, Observable} from "rxjs";
import {catchError, map, tap} from "rxjs/operators";
import {Payload} from "./payload/payload";
import {EntityCollectionGetByIdService} from "./entityCollectionGetByIdService";
import {HttpModelConverter} from "../converter/httpModel.converter";
import {ToastControl} from "../../../controls/core/toast/toast.control";
import {AppInjector} from "../../../app-injector";
import {DefaultDataservice} from "../../../store/core/dataservice/default.dataservice";

export abstract class EntityCollectionCrudService<T, P extends Payload, C extends HttpModelConverter<T>> extends EntityCollectionGetByIdService<T, C>{

    private toastControl: ToastControl;

    // Create / Edit
    readonly saveLoading$: Observable<boolean> = new BehaviorSubject<boolean>(false);
    readonly saveErrors$: Observable<EntityAction>;
    readonly saveSuccess$: Observable<boolean>;
    readonly savedEntity$: Observable<T>;


    protected constructor(serviceName: string, entityCollectionServiceBase: EntityCollectionServiceBase<T>, dataService: DefaultDataservice<T>, converter: C) {
        super(serviceName, entityCollectionServiceBase, dataService, converter);
        this.toastControl = AppInjector.injector.get(ToastControl);

        // Create / edit
        this.entityActions$.subscribe(entityAction => {
            // // console.logByType[entityAction.payload.tag, entityAction.payload.entityOp]);
            this.setLoading(entityAction.payload.tag === this.serviceName &&
                (entityAction.payload.entityOp === EntityOp.SAVE_ADD_ONE || entityAction.payload.entityOp === EntityOp.SAVE_UPDATE_ONE));

        });

        this.saveErrors$ = this.entityActions$.pipe(
            map(error => error.payload.entityOp === EntityOp.SAVE_ADD_ONE_ERROR || error.payload.entityOp === EntityOp.SAVE_UPDATE_ONE_ERROR ? error : null)
        );
        this.saveSuccess$ = this.entityActions$.pipe(
            map(entityAction => entityAction.payload.entityOp === EntityOp.SAVE_ADD_ONE_SUCCESS || entityAction.payload.entityOp === EntityOp.SAVE_UPDATE_ONE_SUCCESS)
        );
        this.savedEntity$ = this.entityActions$.pipe(
            map((entityAction) => (entityAction.payload.entityOp === EntityOp.SAVE_UPDATE_ONE_SUCCESS || entityAction.payload.entityOp === EntityOp.SAVE_ADD_ONE_SUCCESS) ? this.converter.convert(entityAction.payload.data.changes) : null)
        );
    }

    save(payload: P): Observable<T> {
        let observable: Observable<T>;

        // // console.logByType['save', this.serviceName]);
        if (this.entityCollectionServiceBase.selectId(payload as any)){
            observable = this.entityCollectionServiceBase.update(payload as any, {
                tag: this.serviceName
            });
        } else {
            observable = this.entityCollectionServiceBase.add(payload as any, {
                tag: this.serviceName
            });
        }
        return observable.pipe(
            tap(() => {
                this.getServicesToSynchronize().forEach(value => value.clearCache());
            }),
            map((entity: T) => this.converter.convert(entity)));
    }

    executeAction(id: string | number, actionName: string, payload?: any, errorMessage?: string) {
        (this.saveLoading$ as BehaviorSubject<boolean>).next(true);
        this.executeUpdateOne(id, 'action-' + actionName, payload, errorMessage);
    }

    setSaveLoading(loading: boolean) {
        (this.saveLoading$ as BehaviorSubject<boolean>).next(loading);
    }

    protected executeSave(id: number | string, payload: any, errorMessage?: string, path?: string): Observable<T> {
        // console.logByType['execute save', id]);
        return id ? this.executeUpdateOne(id, payload, errorMessage, path) : this.executeCreate(payload, errorMessage, path);
    }

    // FIXME handle loading propely
    protected executeCreate(payload: any, errorMessage?: string, path?: string): Observable<T> {
        return this.entityDataService.executeCreate(payload, path)
            .pipe(
                map((entity: T) => this.converter.convert(entity)),
                tap(entity => this.entityCollectionServiceBase.addOneToCache(entity)),
                tap(entity => {
                    const action = this.entityCollectionServiceBase.dispatcher.createEntityAction(EntityOp.SAVE_ADD_ONE_SUCCESS, this.buildEntityActionDataForCreate(entity));

                    // // console.logByType['executeUpdateOne', entity, action]);
                    this.getServicesToSynchronize().forEach(value => value.clearCache());
                    this.entityCollectionServiceBase.dispatch(action);
                }),
                catchError((e) => {
                    const action = this.entityCollectionServiceBase.dispatcher.createEntityAction(EntityOp.SAVE_ADD_ONE_ERROR);
                    this.entityCollectionServiceBase.dispatch(action);
                    this.toastControl.presentError(e, errorMessage ? errorMessage : 'Une erreur est survenue');
                    throw e;
                }))
    }

    protected executeUpdateOne(id: number | string, payload: any, errorMessage?: string, path?: string): Observable<T> {
        const options: EntityActionOptions = {
            tag: this.serviceName,
        };

        return this.entityDataService.executeUpdate(id, path, payload)
                .pipe(
                    map((entity: T) => this.converter.convert(entity)),
                    tap(entity => this.entityCollectionServiceBase.updateOneInCache(entity, options)),
                    tap(entity => {
                        // console.logByType['entity', entity]);
                        const action = this.entityCollectionServiceBase.dispatcher.createEntityAction(EntityOp.SAVE_UPDATE_ONE_SUCCESS,
                            this.buildEntityActionDataForUpdate(entity));

                        // // console.logByType['executeUpdateOne', entity, action]);
                        this.getServicesToSynchronize().forEach(value => value.clearCache());
                        this.entityCollectionServiceBase.dispatch(action);
                }),
                catchError((e) => {
                    const action = this.entityCollectionServiceBase.dispatcher.createEntityAction(EntityOp.SAVE_UPDATE_ONE_ERROR);
                    this.entityCollectionServiceBase.dispatch(action);
                    this.toastControl.presentError(e, errorMessage ? errorMessage : 'Une erreur est survenue');
                    throw e;
                }))
    }

    private buildEntityActionDataForUpdate(entity: T) {
        return {
            id: this.entityCollectionServiceBase.selectId(entity),
            changes: entity,
        }
    }

    protected getServicesToSynchronize(): EntityCollectionServiceBase<any>[] {
        return [];
    }

    private buildEntityActionDataForCreate(entity: T) {
        return entity;
    }


}